import type {parsePaymentPlan} from '../payment';
import type {CoCoachFields} from './types';

import {FULL_PORTION} from './constants';
import {fillPortionsToFull} from './fillPortionsToFull';
import {getPortionPriceString} from './getPortionPriceString';

export const distributePortions = <T extends CoCoachFields>({
  coach,
  coaches,
  parsedPaymentPlan,
  portion,
  price,
}: {
  coach: T;
  coaches: T[];
  parsedPaymentPlan: ReturnType<typeof parsePaymentPlan>;
  portion: number;
  price: string;
}) => {
  let newCoaches = coaches.slice();

  if (portion === FULL_PORTION) {
    newCoaches = newCoaches.map(_coach => {
      if (_coach._id === coach._id) {
        return {
          ..._coach,
          portion,
          price: getPortionPriceString(portion, parsedPaymentPlan),
        };
      } else {
        return {
          ..._coach,
          portion: 0,
          price: getPortionPriceString(0, parsedPaymentPlan),
        };
      }
    });
  } else if (coach.is_owner) {
    newCoaches = newCoaches.map(_coach => {
      if (_coach.is_owner) {
        return {
          ..._coach,
          portion,
          price:
            portion <= FULL_PORTION
              ? price
              : getPortionPriceString(portion, parsedPaymentPlan),
        };
      } else {
        let cocoachPortion =
          _coach.portion + (coach.portion - portion) / (newCoaches.length - 1);

        cocoachPortion = cocoachPortion >= 0 ? cocoachPortion : 0;

        return {
          ..._coach,
          portion: cocoachPortion,
          price: getPortionPriceString(cocoachPortion, parsedPaymentPlan),
        };
      }
    });

    newCoaches = fillPortionsToFull(newCoaches);
  } else {
    // co-coach branch
    let owner: T | undefined;
    let otherCocoachesPortions = 0;

    newCoaches.forEach(_coach => {
      if (_coach.is_owner) {
        owner = _coach;
      } else if (_coach._id !== coach._id) {
        otherCocoachesPortions += _coach.portion;
      }
    });

    const maxPortion = FULL_PORTION - otherCocoachesPortions;

    let ownerPortion = owner!.portion + (coach.portion - portion);

    ownerPortion = ownerPortion >= 0 ? ownerPortion : 0;

    // we share portion between a co-coach and main coach only
    newCoaches = newCoaches.map(_coach => {
      // co-coach
      if (_coach._id === coach._id) {
        return {
          ..._coach,
          portion: portion <= maxPortion ? portion : maxPortion,
          price:
            portion <= maxPortion
              ? price
              : getPortionPriceString(maxPortion, parsedPaymentPlan),
        };
        // owner
      } else if (_coach.is_owner) {
        return {
          ..._coach,
          portion: ownerPortion,
          price: getPortionPriceString(ownerPortion, parsedPaymentPlan),
        };
      } else {
        return _coach;
      }
    });

    newCoaches = fillPortionsToFull(newCoaches);
  }

  return newCoaches;
};
